import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  useBreakpointValue,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import axiosInstance from "../../config/axiosInstance";

const Create = () => {
  const boxWidth = useBreakpointValue({ base: "90%", sm: "xs", md: "lg" });
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    securityCode: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateInputs = () => {
    const { name, email, phone, password, confirmPassword, securityCode } = form;

    if (!name.trim() || name.length < 3) {
      toast({ title: "Full name must be at least 3 characters.", status: "warning", isClosable: true });
      return false;
    }

    if (!/^[a-zA-Z\s]+$/.test(name)) {
      toast({ title: "Name should contain only letters and spaces.", status: "warning", isClosable: true });
      return false;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: "Invalid email format.", status: "warning", isClosable: true });
      return false;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast({ title: "Phone number must be 10 digits.", status: "warning", isClosable: true });
      return false;
    }

    if (password.length < 6) {
      toast({ title: "Password must be at least 6 characters.", status: "warning", isClosable: true });
      return false;
    }

    if (password !== confirmPassword) {
      toast({ title: "Passwords do not match.", status: "error", isClosable: true });
      return false;
    }

    if (!securityCode.trim()) {
      toast({ title: "Security code is required.", status: "warning", isClosable: true });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInputs()) return;

    try {
      await axiosInstance.post("/api/auth/register", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        role: "admin",
        securityCode: form.securityCode,
      });

      toast({
        title: "Admin registered successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/login");
    } catch (err) {
      toast({
        title: "Registration failed",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex minH="100vh" bg="gray.50" px={2} justify="center" align="start" pt={4}>
      <Box
        bg="white"
        w={boxWidth}
        rounded="xl"
        p={{ base: 4, md: 6 }}
        boxShadow="lg"
        pt={8}
      >
        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <Heading fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" textAlign="center">
              Admin Registration
            </Heading>

            <Text fontSize="xs" textAlign="center">
              Already have an account?{" "}
              <Link as={RouterLink} to="/login" color="blue.500">
                Sign In
              </Link>
            </Text>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Full Name</FormLabel>
              <Input name="name" placeholder="Enter full name" size="sm" onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Email</FormLabel>
              <Input name="email" type="email" placeholder="Enter email" size="sm" onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Phone Number</FormLabel>
              <Input name="phone" type="tel" placeholder="Enter phone number" size="sm" onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Password</FormLabel>
              <Input name="password" type="password" placeholder="Create password" size="sm" onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Confirm Password</FormLabel>
              <Input
                name="confirmPassword"
                type="password"
                placeholder="Confirm password"
                size="sm"
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Admin Security Code</FormLabel>
              <Input
                name="securityCode"
                type="text"
                placeholder="Enter admin security code"
                size="sm"
                onChange={handleChange}
              />
            </FormControl>

            <Button
              size="sm"
              color="white"
              bg="#005738"
              _hover={{ bg: "#00462e" }}
              w="full"
              type="submit"
            >
              Create Admin Account
            </Button>

            <Divider />

            <Text fontSize="xs" textAlign="center" color="gray.600">
              By creating an account, you agree to our{" "}
              <Link href="#" color="blue.500">
                Terms of Service
              </Link>
            </Text>
          </VStack>
        </form>
      </Box>
    </Flex>
  );
};

export default Create;
